/*
 * A2M Notify — safe, in-place staged signup.
 *
 * V21: Shopify Forms request FIRST; optional Postscript SMS AFTER Shopify
 * confirms success. The existing custom CTA can trigger the real native Forms
 * button only when the app's accessible DOM provides a safe target.
 * No contact-form POST, no undocumented API, no artificial success state.
 * V27.1: safeguard Shopify CAPTCHA initialization and native verification
 * failures; never show A2M success until Shopify Forms actually confirms.
 * If the native app UI cannot be automated, reveal it so the customer can
 * complete the request normally; SMS is never a submission condition.
 */
(() => {
  'use strict';

  if (window.__a2mnInitialized) return;
  window.__a2mnInitialized = true;

  const POSTSCRIPT_SHOP = '597650';
  const POSTSCRIPT_FORM = '3ab4002b-177d-4086-880a-aa4526fc505d';
  let postscriptCompletion = null;
  const controllers = [];

  // Browser-only request history: saved ONLY after a real native Shopify
  // success state. Do not store phone numbers, names, emails or comments.
  const HISTORY_KEY = 'a2mn-confirmed-requests-v1';
  const HISTORY_MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  let memoryHistory = [];
  function readRecentHistory() {
    let raw = memoryHistory;
    try { const value=JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      if(Array.isArray(value))raw=value;
    } catch (_) { /* blocked/private storage: keep this-tab memory */ }
    const now=Date.now();
    return raw.filter(item=>item && typeof item==='object' &&
      typeof item.when==='number' && item.when<=now && now-item.when<HISTORY_MAX_AGE)
      .sort((a,b)=>b.when-a.when).slice(0,20);
  }
  function storeRecentHistory(list) {
    memoryHistory=list.slice(0,20);
    try {localStorage.setItem(HISTORY_KEY,JSON.stringify(memoryHistory));} catch (_) {}
  }
  function safeProductUrl(url) {
    if(typeof url!=='string')return '';
    try {const resolved=new URL(url,location.href);
      return resolved.origin===location.origin && /^\/(?:products|collections)\//.test(resolved.pathname)
        ? resolved.href : '';
    } catch (_) {return '';}
  }
  function safeProductImage(source) {
    if (typeof source !== 'string' || !source.trim()) return '';
    try {
      const resolved = new URL(source, location.href);
      // Permit only HTTPS images hosted by this shop or Shopify's own CDN.
      const host = resolved.hostname.toLowerCase();
      return resolved.protocol === 'https:' && (
        resolved.origin === location.origin ||
        host === 'cdn.shopify.com' || host.endsWith('.cdn.shopify.com')
      ) ? resolved.href : '';
    } catch (_) { return ''; }
  }
  function recordRecentHistory(entry) {
    if(!entry || !entry.mode)return;
    const history=readRecentHistory();
    const recent=history[0];
    if(recent && recent.mode===entry.mode && recent.product===entry.product &&
      recent.artist===entry.artist && recent.size===entry.size && Date.now()-recent.when<15000)return;
    history.unshift({...entry,when:Date.now()});
    storeRecentHistory(history);
  }


  // Register before either native embedded form initializes.
  window.addEventListener('postscript:embedded-form', onPostscriptEvent);

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function delay(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }

  function stageVisible(root) {
    const overlay = root.closest('[data-a2mn-preview-root]');
    return !overlay || !overlay.hidden;
  }

  function initUI(root) {
    if (root.dataset.ready === '1') return;
    root.dataset.ready = '1';

    const host = root.closest('[data-a2mn-stage-host]');
    if (!host) return;

    const get = selector => host.querySelector(selector);
    const heading = get('[data-a2mn-heading]');
    const title = get('[data-a2mn-title]');
    const description = get('[data-a2mn-description]');
    const product = get('[data-a2mn-product]');
    const size = get('[data-a2mn-size]');
    const sizeField = get('[data-a2mn-size-field]');
    const firstName = get('[data-a2mn-first-name]');
    const email = get('[data-a2mn-email]');
    const comments = get('[data-a2mn-comments]');
    const wantsSms = get('[data-a2mn-wants-sms]');
    // V23: field labels are presentation only; native Shopify Forms still owns
    // validation. Each of the three A2M tabs has its own editable label copy.
    const editableLabels = {
      product: get('[data-a2mn-field-label="product"]'),
      size: get('[data-a2mn-field-label="size"]'),
      first_name: get('[data-a2mn-field-label="first_name"]'),
      email: get('[data-a2mn-field-label="email"]'),
      comments: get('[data-a2mn-field-label="comments"]'),
      sms: get('[data-a2mn-sms-choice-label]')
    };
    const requireProduct = root.getAttribute('data-a2mn-require-product') === 'true';
    const sizePlaceholder = root.getAttribute('data-a2mn-size-placeholder') || 'select a size';

    function updateFieldLabels() {
      for (const [field, node] of Object.entries(editableLabels)) {
        if (!node) continue;
        const setting = root.getAttribute(`data-a2mn-label-${field.replace(/_/g, '-')}-${mode}`);
        if (setting === null) continue;
        node.textContent = setting;
        // Empty label deliberately hides its visual wrapper, not the input.
        // Semantic aria-labels remain on the actual form controls.
        if (field === 'sms') {
          node.hidden = !setting.trim();
        } else {
          const labelElement = node.closest('label');
          if (labelElement) labelElement.hidden = !setting.trim();
        }
      }
    }

    const status = get('[data-a2mn-status]');
    const submit = get('[data-a2mn-submit]');
    const sms = get('[data-a2mn-sms-native]');
    const smsStatus = get('[data-a2mn-sms-status]');
    const shopify = get('[data-a2mn-shopify-stage]');
    const recap = get('[data-a2mn-recap]');
    const loader = get('[data-a2mn-transition-loader]');
    const appSlot = get('[data-a2mn-shopify-app-block]');
    const formSetup = get('[data-a2mn-shopify-setup]');
    const shopifyStatus = get('[data-a2mn-shopify-status]');
    const shopifyHelp = get('[data-a2mn-final-help]');
    const shopifyHeading = get('[data-a2mn-shopify-heading]');
    const screens = {details: root, sms, shopify, submitting: loader};

    let mode = 'notify';
    let stage = 'details';
    let switching = false;
    let processedComplete = false;
    let shopifyConfirmed = false;
    let nativeSubmissionPending = false;
    let nativeFallbackTimer = 0;
    let formWatchTimer = 0;
    let lastNativeStep = null;
    let nativeObservers = [];
    let watchedRoots = new WeakSet();
    let captchaPrepared = new WeakSet();
    let nativeFormListeners = new WeakSet();
    let nativeErrorShown = false;
    let selectedProductHasSizes = false;
    let productSizeRequestPending = false;
    const artist = get('[data-a2mn-artist]');
    const artistField = get('[data-a2mn-artist-field]');
    const lastName = get('[data-a2mn-last]');
    const phone = get('[data-a2mn-phone]');
    const rootSettings = host.closest('[data-a2mn-preview-root],[data-a2mn-page]');
    function parseConfig(selector, fallback) {
      try { return JSON.parse(root.querySelector(selector)?.textContent || ''); }
      catch (_) { return fallback; }
    }
    const modeConfigs = parseConfig('[data-a2mn-block-config]', {});
    const soldoutCatalog = parseConfig('[data-a2mn-soldout-products]', []);
    const productsByHandle = new Map(soldoutCatalog.map(item => [item.handle, item]));
    const wrappers = {
      product: get('[data-a2mn-product-field]'),artist:artistField,size:sizeField,
      first:get('[data-a2mn-first-field]'),last:get('[data-a2mn-last-field]'),
      email:get('[data-a2mn-email-field]'),phone:get('[data-a2mn-phone-field]'),
      comments:get('[data-a2mn-comments-field]'),sms:get('[data-a2mn-sms-choice]')
    };
    const fields = {product,artist,size,first:firstName,last:lastName,email,phone,comments,sms:wantsSms};
    const names = {restock:'restock',notify:'notify',early:'early'};
    const fallbacks = {
      restock:{heading:"don't miss the next restock",description:'be the first to know when {product} becomes available again.',action:'request restock'},
      notify:{heading:"don't miss the next wav",description:'be the first to know when {product} becomes available.',action:'notify me'},
      early:{heading:"don't miss the next wav",description:'be first in line for early access to the next wav.',action:'request early access'}
    };
    function fieldOptions(key) {
      const explicit=modeConfigs[mode]?.fields?.[key];
      if (explicit) return explicit;
      const legacyKey = key === 'first' ? 'first-name' : key;
      const legacy = root.getAttribute(`data-a2mn-label-${legacyKey}-${mode}`);
      const labels = {first:'first name',last:'last name',email:'email address',product:'product',artist:'requested artist',size:'size',phone:'phone number',comments:'anything you want us to know?',sms:'select this if you want to receive restock alerts, early access, free priority shipping, & exclusive offers up to 50% off :)'};
      return {visible:true,star:false,gate:key==='first'||key==='email'||(mode==='restock'&&['product','size','artist'].includes(key)),label:legacy&&key!=='sms'?legacy:labels[key],placeholder:fields[key]?.getAttribute('placeholder') || ''};
    }
    function modeConfig() {return modeConfigs[mode] || fallbacks[mode];}
    function selectedProduct() {return productsByHandle.get(product.selectedOptions[0]?.dataset.handle) || null;}
    function isEnthusiast() {return /enthusiast/i.test(selectedProduct()?.title || '');}
    function optionIndex(options, type) {
      return (options||[]).findIndex(value => type==='size' ? /^sizes?$/i.test(value) : /artist|design|graphic/i.test(value));
    }
    function fillSelect(select, placeholder, values) {
      const previous=select.value;
      select.replaceChildren(new Option(placeholder,''));
      for(const value of values) select.add(new Option(value,value));
      if(values.includes(previous)) select.value=previous;
    }
    function updateVariantSelectors(resetArtist=false) {
      const item=selectedProduct();
      const variants=item?.variants||[];
      const artistIndex=isEnthusiast()?optionIndex(item.options,'artist'):-1;
      const sizeIndex=optionIndex(item?.options,'size');
      if(resetArtist) artist.value='';
      const artists=artistIndex>=0?[...new Set(variants.map(v=>v[`option${artistIndex+1}`]).filter(Boolean))]:[];
      fillSelect(artist,fieldOptions('artist').placeholder || 'select an artist',artists);
      const variantSet=variants.filter(v=>artistIndex<0 || !artist.value || v[`option${artistIndex+1}`]===artist.value);
      const sizes=sizeIndex>=0?[...new Set(variantSet.map(v=>v[`option${sizeIndex+1}`]).filter(Boolean))]:[];
      fillSelect(size,fieldOptions('size').placeholder || 'select a size',sizes);
      selectedProductHasSizes=Boolean(sizes.length);
      return {artists,sizes};
    }
    function fieldReady(key,el) {
      if(key==='sms') return true;
      if(key==='email') return Boolean(el?.value.trim() && el.checkValidity());
      if(key==='phone') return Boolean(el?.value.replace(/\D/g,'').length>=10);
      return Boolean(el?.value.trim());
    }
    function readyToContinue() {
      if(productSizeRequestPending)return false;
      // "Gate CTA" is independent of Shopify's required field metadata.
      // If Shopify itself still requires a field, that validation remains native.
      return Object.entries(fields).every(([key,el])=>{
        if(!el || wrappers[key]?.hidden || !fieldOptions(key).gate)return true;
        return fieldReady(key,el);
      });
    }
    function updateButtonReadiness() {
      const ready=readyToContinue();
      submit.disabled=!ready;
      submit.setAttribute('aria-disabled',String(!ready));
    }
    function applyStageCopy() {
      if(!rootSettings)return;
      const data=rootSettings.dataset;
      const set=(sel,value)=>{const node=get(sel);if(node&&value!==undefined)node.textContent=value;};
      set('[data-a2mn-loading-label]',data.v24LoaderCopy||'one moment');
      set('[data-a2mn-pending-title] span',data.v24PendingHeading||'request confirmation');
      set('[data-a2mn-pending-copy]',data.v24PendingCopy||'your request is being saved.');
      // A dedicated SMS Phone Screen block overrides the original shared
      // section settings; the OTP block takes over after Postscript renders it.
      const entry=get('[data-a2mn-sms-native]');
      if(entry){
        entry.dataset.a2mnSmsStep='phone';
        entry.dataset.a2mnBackVisible=data.a2mnPhoneShowBack||'false';
        entry.dataset.a2mnSmsBlockGlyph=data.a2mnPhoneNote||data.v24SmsNoteShow||'true';
        entry.dataset.a2mnSmsBlockSavedGlyph=data.a2mnPhoneSavedNote||data.v24SavedNoteShow||'true';
      }
      set('[data-a2mn-sms-native] .a2mn-sms-heading span',data.a2mnPhoneHeading||data.v24SmsHeading||'verify your text updates');
      set('[data-a2mn-sms-native] .a2mn-sms-explainer',data.a2mnPhoneDescription||data.v24SmsCopy||'enter your number to receive updates.');
      set('[data-a2mn-saved-text]',data.a2mnPhoneSaved||data.v24SmsSavedCopy||'your request has been submitted. text updates are optional.');
      set('[data-a2mn-sms-back-label]',data.a2mnPhoneBack||data.v24SmsBackCopy||'back to my request');
      set('[data-a2mn-sms-skip-label]',data.a2mnPhoneSkip||data.v24SmsSkipCopy||'skip text updates & view request confirmation');
      const savedNote=get('[data-a2mn-saved-prompt] .a2mn-inline-note');
      if(savedNote)savedNote.hidden=(data.a2mnPhoneSavedNote||data.v24SavedNoteShow)==='false';
      set('[data-a2mn-history-clear]',data.v24HistoryClear||'clear recent requests');
      set('[data-a2mn-success-title]',data.v24SuccessHeading||"we've received your request!");
      set('[data-a2mn-summary-label]',data.v24SummaryTitle||'view request:');
      const shopping=get('[data-a2mn-continue-shopping]');
      if(shopping){shopping.replaceChildren(document.createTextNode((data.v24SuccessShopping||'continue shopping')+' '));let span=document.createElement('span');span.setAttribute('aria-hidden','true');span.textContent='→';shopping.append(span);}
    }
    applyStageCopy();

    // V26: finger-tracked selection with an unscaled, fully rounded pill.
    // No animation on tap; only a horizontal drag moves the pill. Content is
    // updated ONCE on release, not every time the finger crosses a tab.
    function initSlidingTabs() {
      const tabs = root.querySelector('.a2mn-tabs');
      if (!tabs) return () => {};
      let pill = tabs.querySelector('.a2mn-tabs-indicator');
      if (!pill) {
        pill = document.createElement('span');
        pill.className = 'a2mn-tabs-indicator';
        pill.setAttribute('aria-hidden', 'true');
        tabs.prepend(pill);
      }
      const buttons = [...tabs.querySelectorAll('button[data-mode]')];
      let gesture = null;
      let frame = 0;
      let blockedClick = false;

      function metrics(button) {
        const a = tabs.getBoundingClientRect();
        const b = button.getBoundingClientRect();
        return {x:b.left-a.left,y:b.top-a.top,width:b.width,height:b.height};
      }
      function paint(x, y, width, height) {
        if (!pill.isConnected) return;
        // Never scaleX: scaling is what distorted the corner radius.
        pill.style.width = Math.max(1,width)+'px';
        pill.style.height = Math.max(1,height)+'px';
        pill.style.transform = `translate3d(${x}px,${y}px,0)`;
      }
      function selected() {return buttons.find(b=>b.classList.contains('is-active')) || buttons[0];}
      function position() {
        if (gesture?.dragging) return;
        const active = selected();
        if (!active || !tabs.isConnected || !active.offsetWidth) return;
        const m = metrics(active);
        paint(m.x,m.y,m.width,m.height);
        tabs.classList.add('a2mn-tabs--slider-ready');
      }
      function schedule() {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {frame=0;position();});
      }
      function endGesture(event, cancelled=false) {
        if (!gesture || event.pointerId !== gesture.id) return;
        const g = gesture;
        gesture = null;
        tabs.classList.remove('a2mn-tabs--finger-dragging');
        try { if(tabs.hasPointerCapture(event.pointerId))tabs.releasePointerCapture(event.pointerId); } catch(_) {}
        if (!g.dragging) {schedule();return;}
        blockedClick = true;
        if (cancelled) {position();return;}
        const center = event.clientX;
        const closest = buttons.reduce((best,button)=> {
          const r=button.getBoundingClientRect();
          const distance=Math.abs(center-(r.left+r.width/2));
          return distance<best.distance ? {button,distance} : best;
        },{button:buttons[0],distance:Infinity}).button;
        if (closest && closest.dataset.mode !== mode) {
          mode = closest.dataset.mode;
          updateFields();
        }
        // Selection snaps to the correct natural width without distortion.
        position();
      }
      // V27: The tab strip owns the full lifetime of a one-finger touch.
      // Safari must not turn a slow / slightly diagonal pill swipe into a
      // vertical page pan. Don't prevent touchstart: ordinary taps must still
      // click a tab, and scrolling outside this strip remains native-speed.
      tabs.addEventListener('touchmove', event => {
        if (event.touches.length !== 1) return;
        if (event.cancelable) event.preventDefault();
        // Do not let an ancestor manual scroll handler receive this gesture.
        event.stopPropagation();
      }, {passive:false});
      tabs.addEventListener('pointerdown', event => {
        if (event.button !== 0 || !event.isPrimary) return;
        const button = event.target.closest?.('button[data-mode]');
        if (!button || !tabs.contains(button)) return;
        blockedClick = false;
        const m=metrics(selected());
        gesture={id:event.pointerId,originX:event.clientX,originY:event.clientY,
          start:m,dragging:false,pendingX:m.x};
        // Capture only after a real drag, so a normal tap still clicks
        // the underlying button immediately (pointer capture steals taps).
      });
      tabs.addEventListener('pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const g=gesture;
        const dx=event.clientX-g.originX;
        const dy=event.clientY-g.originY;
        if (!g.dragging) {
          // The gesture belongs to the tabs even when the finger drifts
          // vertically. Wait for horizontal intent, never yield to scrolling.
          if (Math.abs(dx) < 6) return;
          g.dragging=true;
          if (frame) {cancelAnimationFrame(frame);frame=0;}
          tabs.classList.add('a2mn-tabs--finger-dragging');
          try {tabs.setPointerCapture(event.pointerId);} catch(_) {}
        }
        if (event.cancelable) event.preventDefault();
        const bounds=buttons.map(metrics);
        const min=Math.min(...bounds.map(b=>b.x));
        const max=Math.max(...bounds.map(b=>b.x+b.width))-g.start.width;
        g.pendingX=Math.max(min,Math.min(max,g.start.x+dx));
        if (!frame) frame=requestAnimationFrame(()=> {
          frame=0;
          if (gesture === g) paint(g.pendingX,g.start.y,g.start.width,g.start.height);
        });
      });
      tabs.addEventListener('pointerup', event => endGesture(event));
      tabs.addEventListener('pointercancel', event => endGesture(event,true));
      // iOS sends a synthetic click after a drag. Consume it so selection
      // can't unexpectedly switch a second time and cause a flicker.
      tabs.addEventListener('click', event => {
        if (!blockedClick) return;
        blockedClick=false;
        event.preventDefault();
        event.stopImmediatePropagation();
      },true);
      if (typeof ResizeObserver === 'function') {
        const observer=new ResizeObserver(schedule);
        observer.observe(tabs);
        buttons.forEach(button=>observer.observe(button));
      }
      window.addEventListener('resize',schedule,{passive:true});
      if (document.fonts?.ready) document.fonts.ready.then(schedule);
      schedule();
      return position;
    }

    const moveTabPill = initSlidingTabs();

    function updateFields() {
      const cfg=modeConfig();
      const name=product.value ? (/^the(?:\s|$)/i.test(product.value)?product.value:'the '+product.value) : 'your selected product';
      heading.textContent=cfg.heading || fallbacks[mode].heading;
      title.dataset.restock=String(mode==='restock');
      description.textContent=(cfg.description||fallbacks[mode].description).replace(/\{product\}/gi,name);
      const opts=updateVariantSelectors(false);
      if(product.options[0]&&!product.options[0].value)product.options[0].textContent=fieldOptions('product').placeholder||'product name';
      for(const [key,wrapper] of Object.entries(wrappers)) {
        if(!wrapper)continue;
        const setting=fieldOptions(key);
        let allowed=setting.visible!==false;
        if(key==='artist')allowed=allowed && isEnthusiast() && opts.artists.length>0;
        if(key==='size')allowed=allowed && opts.sizes.length>0 && (mode==='restock' || setting.visible===true);
        wrapper.hidden=!allowed;
        const label= key==='sms' ? get('[data-a2mn-sms-choice-label]') : wrapper.querySelector('label');
        if(label) {
          label.textContent=setting.label||'';
          if(setting.star){const star=document.createElement('span');star.className='a2mn-required-mark';star.textContent=' *';star.setAttribute('aria-hidden','true');label.append(star);}
          label.hidden=!String(setting.label||'').trim();
        }
        const field=fields[key];
        if(field && key!=='sms' && setting.placeholder!==undefined && setting.placeholder!==null && !['product','size','artist'].includes(key))field.placeholder=setting.placeholder;
      }
      const label=submit.querySelector('[data-a2mn-cta-label]');
      if(label)label.textContent=cfg.action||fallbacks[mode].action;
      root.querySelectorAll('[data-mode]').forEach(button=>{
        const active=button.dataset.mode===mode;
        button.classList.toggle('is-active',active);
        button.setAttribute('aria-selected',String(active));
        const tabCfg=modeConfigs[button.dataset.mode];
        const nextLabel=tabCfg?.tabLabel||names[button.dataset.mode];
        if(button.textContent!==nextLabel)button.textContent=nextLabel;
      });
      updateButtonReadiness();
      moveTabPill();
    }

    root.querySelectorAll('[data-mode]').forEach(button => {
      button.addEventListener('click', () => {
        if (mode === button.dataset.mode) return;
        mode = button.dataset.mode;
        updateFields();
      });
    });

    product.addEventListener('change',()=>{
      artist.value='';size.value='';
      updateFields();queueNativeFieldSync();
    });
    artist.addEventListener('change',()=>{size.value='';updateFields();queueNativeFieldSync();});
    size.addEventListener('change',()=>{updateButtonReadiness();queueNativeFieldSync();});
    [firstName,lastName,email,phone,comments].forEach(field=>{
      field?.addEventListener('input',updateButtonReadiness);
      field?.addEventListener('change',updateButtonReadiness);
    });

    // Mirror A2M details into accessible Shopify Forms inputs while typing.
    // Never submit, click Shopify controls, or alter native consent here.
    let pendingFieldSync = 0;
    const queueNativeFieldSync = () => {
      if (pendingFieldSync || shopifyConfirmed) return;
      pendingFieldSync = requestAnimationFrame(() => {
        pendingFieldSync = 0;
        const found = shopifyDOM();
        if (found.emailInput && found.firstNameInput) syncNativeShopifyFields(found);
      });
    };
    [firstName, lastName, email, phone, product, artist, size, comments].forEach(field => {
      field?.addEventListener('input', queueNativeFieldSync);
      field?.addEventListener('change', queueNativeFieldSync);
    });

    // Only the user's explicit first-button click starts the signup.
    function validDetails() {
      status.textContent='';
      updateButtonReadiness();
      if(!readyToContinue())return false;
      return true;
    }

    function loadingImage() {
      const overlay = host.closest('[data-a2mn-preview-root]');
      const source = overlay?.querySelector('[data-a2mn-loader-image]')
        || host.closest('[data-a2mn-page]')?.querySelector('[data-a2mn-page-loader-source]');
      return source ? source.cloneNode(true) : null;
    }

    function loaderDuration() {
      const container = host.closest('[data-a2mn-preview-root]')
        || host.closest('[data-a2mn-page]');
      const chosen = Number(container?.dataset.loaderDuration);
      return reducedMotion() ? 0 : Number.isFinite(chosen) && chosen >= 0
        ? Math.min(chosen, 2000)
        : 500;
    }

    function setScreenHidden(screen, hidden) {
      if (!screen) return;
      if (screen === shopify) {
        // The real Shopify app must remain mounted during initial submission.
        // Its content is visually offstage until we show its real success UI
        // or need the customer to complete native validation manually.
        shopify.dataset.a2mnOffstage = String(hidden);
        shopify.setAttribute('aria-hidden', String(hidden));
        shopify.hidden = false;
      } else {
        screen.hidden = hidden;
      }
    }

    function showLoadingImage() {
      // This is the IN-FORM "one moment" transition, not the configurable
      // initial drawer-opening screen. Its text stays still; only dots pulse.
      loader.querySelectorAll('img[data-a2mn-dynamic-loader]').forEach(node => node.remove());
      const spinner = loader.querySelector('.a2mn-spinner');
      if (spinner) spinner.hidden = true;
      const note = loader.querySelector('.a2mn-loader-note');
      if (note) note.hidden = true;
      const label = loader.querySelector('[data-a2mn-loading-label]');
      if (!label) return;
      const setting = rootSettings?.dataset.v24LoaderCopy || 'one moment';
      const text = setting.trim().replace(/\.{1,3}\s*$/, '').trim() || 'one moment';
      label.textContent = text;
      const dots = document.createElement('span');
      dots.className = 'a2mn-loading-dots';
      dots.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 3; i++) {
        const dot = document.createElement('span');
        dot.textContent = '.';
        dots.appendChild(dot);
      }
      label.appendChild(dots);
    }

    async function showStage(next, withLoader = true) {
      if (!screens[next] || switching || stage === next) return;
      switching = true;
      const old = screens[stage];
      const incoming = screens[next];
      if (old) setScreenHidden(old, true);
      if (withLoader && next !== 'submitting') {
        showLoadingImage();
        loader.hidden = false;
        await delay(loaderDuration());
      }
      if (next === 'submitting') showLoadingImage();
      loader.hidden = next !== 'submitting';
      setScreenHidden(incoming, false);
      stage = next;
      host.dataset.a2mnStage = stage;
      incoming.classList.remove('a2mn-enter');
      // The loader itself never needs a fade-in animation.
      if (next !== 'submitting') {
        void incoming.offsetWidth;
        incoming.classList.add('a2mn-enter');
      }
      const stageScroll = host.closest('[data-a2mn-scroll]');
      if (stageScroll) {
        stageScroll.scrollTop = 0;
        requestAnimationFrame(() => { if (stageScroll.isConnected) stageScroll.scrollTop = 0; });
      }
      switching = false;
      if (next === 'shopify') {
        window.dispatchEvent(new Event('a2mn:claim-shopify-form'));
        refreshShopifyFormAvailability();
      }
      if (next === 'sms' && postscriptCompletion && !processedComplete) {
        onVerifiedPostscript(postscriptCompletion);
      }
    }

    function refreshShopifyFormAvailability() {
      if (!appSlot || !formSetup) return;
      const appPresent = Boolean(appSlot.querySelector(
        'form-embed,shopify-forms,.shopify-app-block,[id^="shopify-block-"],[data-shopify-forms]'
      ));
      formSetup.hidden = appPresent;
      appSlot.dataset.a2mnConnected = String(appPresent);
    }

    function currentRequestSnapshot() {
      const item=selectedProduct();
      // This is non-identifying request data only; not the customer's PII.
      return {mode:mode==='early'?'early access':mode==='restock'?'restock':'notify me',
        product:(product.value||'').slice(0,150),
        artist:artistField?.hidden?'':(artist.value||'').slice(0,80),
        size:sizeField?.hidden?'':(size.value||'').slice(0,30),
        url:safeProductUrl(item?.url),
        image:safeProductImage(item?.image)};
    }
    function drawRecentHistory() {
      const list=get('[data-a2mn-request-history]');
      if(!list)return;
      const clear=get('[data-a2mn-history-clear]');
      const currentDetails=get('[data-a2mn-summary-details]');
      const currentPhoto=get('[data-a2mn-summary-product-link]');
      const limit=Math.max(1,Math.min(20,Number(rootSettings?.dataset.v24HistoryLimit)||8));
      const records=readRecentHistory().slice(0,limit);
      list.replaceChildren();
      if(!records.length){
        list.hidden=false;
        const empty=document.createElement('p');empty.className='a2mn-history-empty';
        empty.textContent=rootSettings?.dataset.v24HistoryEmpty||'no recent requests saved in this browser.';
        list.append(empty);
        if(clear)clear.hidden=true;
        if(currentDetails)currentDetails.hidden=true;
        if(currentPhoto)currentPhoto.hidden=true;
        return;
      }
      list.hidden=false;
      if(clear)clear.hidden=false;
      if(currentDetails)currentDetails.hidden=true;
      if(currentPhoto)currentPhoto.hidden=true;
      for(const item of records){
        const card=document.createElement('div');card.className='a2mn-history-item';card.setAttribute('role','listitem');
        const text=document.createElement('div');text.className='a2mn-history-item-copy';
        const date=document.createElement('p');date.className='a2mn-history-date';
        date.textContent=new Date(item.when).toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'});
        text.append(date);
        for(const [label,value] of [['request type',item.mode],['product',item.product],['artist',item.artist],['size',item.size]]){
          if(!value)continue;
          const p=document.createElement('p');p.textContent=`${label}: ${value}`;text.append(p);
        }
        card.append(text);
        // Older browser history may predate image saving. Match the current
        // Shopify product catalog when available, but never invent an image.
        const matchingProduct = soldoutCatalog.find(product =>
          String(product.title || '').toLowerCase() === String(item.product || '').toLowerCase());
        const productUrl=safeProductUrl(item.url || matchingProduct?.url);
        const productImage=safeProductImage(item.image || matchingProduct?.image);
        if(productUrl && productImage){
          const a=document.createElement('a');a.href=productUrl;a.className='a2mn-history-image';
          a.setAttribute('aria-label',`View ${item.product||'product'}`);
          const img=document.createElement('img');img.loading='lazy';img.alt=item.product||'product';img.src=productImage;
          a.append(img);card.append(a);
        }
        list.append(card);
      }
    }

    function updateRecap() {
      const item=selectedProduct();
      const summary=get('[data-a2mn-summary-details]');
      const link=get('[data-a2mn-summary-product-link]');
      const image=get('[data-a2mn-summary-image]');
      if(summary){
        summary.replaceChildren();
        const entries=[['request type',mode==='early'?'early access':mode==='restock'?'restock':'notify me'],['product',product.value],['artist',artistField.hidden?'':artist.value],['size',sizeField.hidden?'':size.value]];
        for(const [key,value] of entries){if(!value)continue;const line=document.createElement('p');line.textContent=`${key}: ${value}`;summary.append(line);}
      }
      if(link){
        const enabled=rootSettings?.dataset.v24SummaryImage!=='false' && Boolean(item?.image && item?.url);
        link.hidden=!enabled;
        if(enabled){link.href=item.url;image.src=item.image;image.alt=item.title;}
      }
      const section=get('[data-a2mn-summary]');
      if(section)section.hidden=rootSettings?.dataset.v24SummaryShow==='false';
      drawRecentHistory();
      const text=get('[data-a2mn-success-copy]');
      if(text){const template=rootSettings?.dataset.v24SuccessCopy||'your {request_type} request is saved.';
        text.textContent=template.replace(/\{request_type\}/gi,mode==='early'?'early access':mode==='restock'?'restock':'notify me').replace(/\{product\}/gi,product.value||'updates');}
    }

    // Shopify Forms' own app block uses an open Shadow DOM on supported
    // versions. Read ONLY that block, never reach into unrelated forms.
    function shopifyDOM() {
      const roots = [appSlot];
      const visited = new Set(roots);
      for (let i = 0; i < roots.length && i < 18; i++) {
        const current = roots[i];
        if (!current?.querySelectorAll) continue;
        const nodes = current.querySelectorAll('*');
        for (const element of nodes) {
          if (element.shadowRoot && !visited.has(element.shadowRoot)) {
            visited.add(element.shadowRoot);
            roots.push(element.shadowRoot);
          }
        }
      }
      const candidates = roots.flatMap(dom => [...dom.querySelectorAll('input,textarea,select')]);
      const emailInput = candidates.find(input => !input.disabled &&
        (input.type === 'email' || /email/i.test(
          [input.name, input.id, input.getAttribute('autocomplete'), input.getAttribute('aria-label')].join(' ')
        )));
      const firstNameInput = candidates.find(input => !input.disabled && input !== emailInput &&
        (input.getAttribute('autocomplete') === 'given-name' ||
          /\b(first[\s_-]*name|given[\s_-]*name)\b/i.test(labelFor(input, roots))));
      const appSteps = roots.flatMap(dom => [...dom.querySelectorAll('[data-current-step]')]);
      return {roots, candidates, emailInput, firstNameInput, appSteps};
    }

    function labelFor(input, roots) {
      const bits = [input.id, input.name, input.getAttribute('aria-label'), input.getAttribute('placeholder')];
      if (input.id) {
        for (const dom of roots) {
          const label = [...dom.querySelectorAll('label')]
            .find(node => node.htmlFor === input.id);
          if (label) { bits.push(label.textContent); break; }
        }
      }
      if (input.closest('label')) bits.push(input.closest('label').textContent);
      return bits.filter(Boolean).join(' ').replace(/[_-]+/g,' ').toLowerCase();
    }

    function updateNativeField(field, value) {
      if (!field || value === undefined || value === null || field.disabled || field.readOnly) return false;
      if (field.tagName === 'SELECT') {
        const option = [...field.options].find(item =>
          item.value.toLowerCase() === value.toLowerCase() ||
          item.textContent.trim().toLowerCase() === value.toLowerCase());
        if (!option) return false;
        if (field.value === option.value) return true;
        field.value = option.value;
      } else if (field.type === 'checkbox' || field.type === 'radio') {
        // Never change Shopify Forms' own marketing-consent controls.
        return false;
      } else {
        if (field.value === value) return true;
        const setter = Object.getOwnPropertyDescriptor(
          field.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
          'value'
        )?.set;
        if (setter) setter.call(field, value);
        else field.value = value;
      }
      field.dispatchEvent(new Event('input', {bubbles:true, composed:true}));
      field.dispatchEvent(new Event('change', {bubbles:true, composed:true}));
      return true;
    }

    // Shopify may require CAPTCHA for storefront forms. A native form moved
    // into the A2M stage or submitted indirectly may never receive its normal
    // interaction-based initialization. Shopify documents captcha.protect(form)
    // for that case. Attempt it ONCE, only for a real form with an action.
    // Shopify Forms can also use its own verification implementation; this
    // helper must not substitute for, disable, or fake that verification.
    function prepareNativeVerification(found) {
      const nativeForm = found?.emailInput?.closest('form');
      if (!nativeForm) return;

      // Retrying the actual app's button must keep the success observer alive.
      if (!nativeFormListeners.has(nativeForm)) {
        nativeFormListeners.add(nativeForm);
        nativeForm.addEventListener('submit', () => {
          if (shopifyConfirmed || stage !== 'shopify') return;
          nativeSubmissionPending = true;
          nativeErrorShown = false;
          if (!formWatchTimer) formWatchTimer = setInterval(monitorShopifyResult, 700);
          monitorShopifyResult();
        }, true);
      }

      if (!nativeForm.hasAttribute('action') || captchaPrepared.has(nativeForm)) return;
      const protect = window.Shopify?.captcha?.protect;
      if (typeof protect !== 'function') return;
      try {
        protect(nativeForm);
        captchaPrepared.add(nativeForm);
      } catch (error) {
        // Some Shopify Forms app versions implement verification internally.
        // Leave their controls intact instead of bypassing the app.
        console.debug('[A2M Notify] Using native Forms verification', error);
      }
    }

    function nativeVerificationFailure(found) {
      if (!found) return '';
      const messages = found.roots.flatMap(dom => {
        if (!dom?.querySelectorAll) return [];
        return [...dom.querySelectorAll('[role="alert"], [aria-live="assertive"], [class*="error" i]')]
          .filter(node => node instanceof Element && !node.hidden)
          .map(node => (node.textContent || '').replace(/\s+/g, ' ').trim());
      });
      return messages.find(value => /verification was not completed|captcha.*(?:fail|error)|verification.*(?:fail|error)/i.test(value)) || '';
    }

    function syncNativeShopifyFields(found) {
      // Match A2M's required name and email to Shopify's actual form fields.
      // Do not invent values or modify Shopify's marketing-consent controls.
      const syncedName = updateNativeField(found.firstNameInput, firstName?.value.trim());
      const syncedEmail = updateNativeField(found.emailInput, email.value.trim());
      if (!syncedName || !syncedEmail) return false;
      // Optional merchant-created Forms fields. These must exist in the actual
      // Shopify Forms form for product, size or notes to be saved by Flow.
      const fieldMap = [
        [/\b(last[\s_-]*name|family[\s_-]*name)\b/, lastName?.value.trim()||''],
        [/\b(phone|mobile|telephone)\b/, phone?.value.trim()||''],
        [/\b(product|item)\b/, product.value],
        [/\b(requested artist|artist)\b/, artistField.hidden ? '' : artist.value],
        [/\b(requested size|size)\b/, sizeField.hidden ? '' : size.value],
        [/\b(comments?|notes?|message)\b/, comments.value.trim()],
        [/\b(request type|notification type|interest)\b/, mode === 'early' ? 'early access' : mode === 'restock' ? 'restock' : 'notify me']
      ];
      for (const [pattern, value] of fieldMap) {
        if (!value) continue;
        const target = found.candidates.find(field =>
          field !== found.emailInput && !['checkbox','radio','hidden'].includes(field.type) &&
          pattern.test(labelFor(field, found.roots)));
        if (target) updateNativeField(target, value);
      }
      return true;
    }

    function getNativeSubmit(found) {
      if (!found.emailInput) return null;
      const form = found.emailInput.closest('form');
      if (form) {
        const button = form.querySelector('button[type="submit"],input[type="submit"]');
        if (button && !button.disabled) return button;
      }
      // Some Shopify Forms embeds render a submit button outside a <form>.
      // Restrict the search to the SAME shadow root as the native email field.
      const dom = found.emailInput.getRootNode();
      const buttons = [...dom.querySelectorAll('button,input[type="submit"]')];
      return buttons.find(button => !button.disabled &&
        (button.type === 'submit' || /^(submit|sign up|subscribe|notify me|request)/i
          .test((button.textContent || button.value || '').trim()))) || null;
    }

    function nativeSuccessDetected(found) {
      // Never treat a timeout, an SMS event or a generic page text as Shopify
      // success. Shopify's own form must change to its native confirmation UI.
      if (!nativeSubmissionPending || !found) return false;
      const step = found.appSteps.map(node => node.getAttribute('data-current-step')).join('|');
      if (/(^|\|)(success|complete|confirmation|thank.?you)(\||$)/i.test(step)) return true;
      const explicitSuccess = found.roots.some(dom =>
        dom.querySelector('[data-testid*="success"],.form-success,.success-step,[data-form-success],#form-success'));
      if (explicitSuccess) return true;
      // Shopify Forms community examples show #app-embed[data-current-step]
      // changing after a successful submission. Accept an observed step change
      // only when the email form disappears and no validation error is shown.
      const changed = lastNativeStep !== null && step && step !== lastNativeStep;
      if (!changed) return false;
      const visibleErrors = found.roots.some(dom => !!dom.querySelector('[aria-invalid="true"],.error-message,[role="alert"]'));
      const stillEditing = !!found.emailInput && found.emailInput.isConnected &&
        found.emailInput.closest('form')?.querySelector('button[type="submit"]');
      return !visibleErrors && !stillEditing;
    }

    function monitorShopifyResult() {
      if (!nativeSubmissionPending || shopifyConfirmed) return;
      const found = shopifyDOM();
      prepareNativeVerification(found);
      if (lastNativeStep === null && found.appSteps.length) {
        lastNativeStep = found.appSteps.map(node => node.getAttribute('data-current-step')).join('|');
      }
      // Include the shadow roots themselves in MutationObserver coverage.
      for (const dom of found.roots) {
        if (watchedRoots.has(dom)) continue;
        watchedRoots.add(dom);
        const observer = new MutationObserver(monitorShopifyResult);
        observer.observe(dom, {subtree:true,childList:true,attributes:true,characterData:true});
        nativeObservers.push(observer);
      }
      if (nativeSuccessDetected(found)) {
        onShopifyConfirmed();
        return;
      }
      const verificationError = nativeVerificationFailure(found);
      if (verificationError && !nativeErrorShown) {
        nativeErrorShown = true;
        if (nativeFallbackTimer) clearTimeout(nativeFallbackTimer);
        nativeFallbackTimer = 0;
        revealShopifyNative('Shopify could not verify this submission. Please try the native Shopify Forms button below. Your request has not been saved yet.');
      }
    }

    function stopShopifyWatch() {
      if (nativeFallbackTimer) clearTimeout(nativeFallbackTimer);
      if (formWatchTimer) clearInterval(formWatchTimer);
      nativeFallbackTimer = 0;
      formWatchTimer = 0;
      nativeObservers.forEach(observer => observer.disconnect());
      nativeObservers = [];
    }

    function onShopifyConfirmed() {
      if (shopifyConfirmed) return;
      shopifyConfirmed = true;
      nativeSubmissionPending = false;
      nativeErrorShown = false;
      recordRecentHistory(currentRequestSnapshot());
      stopShopifyWatch();
      updateRecap();
      const pending=get('[data-a2mn-pending-confirmation]');
      const confirmed=get('[data-a2mn-confirmation]');
      if(pending)pending.hidden=true;
      if(confirmed)confirmed.hidden=false;
      // Native Shopify success is REAL; hide only its duplicate visual output.
      shopify.dataset.a2mnConfirmed='true';
      if(wantsSms?.checked && !wrappers.sms?.hidden && sms.querySelector('postscript-embedded-form'))showStage('sms');
      else showStage('shopify');
    }

    function revealShopifyNative(reason) {
      if(shopifyConfirmed)return;
      updateRecap();
      shopify.dataset.a2mnConfirmed='false';
      const pending=get('[data-a2mn-pending-confirmation]');
      if(pending)pending.hidden=false;
      const confirmed=get('[data-a2mn-confirmation]');
      if(confirmed)confirmed.hidden=true;
      if(shopifyStatus)shopifyStatus.textContent=reason||'please complete the real Shopify form below.';
      if(shopifyHelp)shopifyHelp.hidden=false;
      showStage('shopify',false);
    }

    function onVerifiedPostscript(detail) {
      // SMS can only supplement a request which Shopify ALREADY accepted.
      if (!shopifyConfirmed || stage !== 'sms' || processedComplete || !stageVisible(host)) return;
      processedComplete = true;
      smsStatus.textContent = detail.subscriberStatus === 'existing'
        ? 'your existing SMS subscription is recognized ♫'
        : 'your SMS subscription is confirmed ♫';
      if (shopifyStatus) shopifyStatus.textContent = 'your request and text updates are confirmed ♫';
      showStage('shopify');
    }

    submit.addEventListener('click', () => {
      if (switching || !['details'].includes(stage) || !validDetails()) return;
      updateRecap();
      window.dispatchEvent(new Event('a2mn:claim-shopify-form'));
      refreshShopifyFormAvailability();
      const found = shopifyDOM();
      prepareNativeVerification(found);
      nativeErrorShown = false;
      const synced = syncNativeShopifyFields(found);
      const nativeButton = synced ? getNativeSubmit(found) : null;
      if (!nativeButton) {
        // This may be a closed/iframe-based app, or a native form whose
        // submit button is disabled until Shopify finishes its own validation.
        // Display the actual block; do not claim a request was submitted.
        nativeSubmissionPending = Boolean(appSlot.querySelector(
          'form-embed,shopify-forms,.shopify-app-block,[id^="shopify-block-"]'
        ));
        lastNativeStep = found.appSteps.map(node => node.getAttribute('data-current-step')).join('|');
        if (nativeSubmissionPending) {
          monitorShopifyResult();
          if (formWatchTimer) clearInterval(formWatchTimer);
          formWatchTimer = setInterval(monitorShopifyResult, 700);
        }
        revealShopifyNative(nativeSubmissionPending
          ? 'finish your request using the Shopify Forms button below. your SMS preference is saved.'
          : 'Shopify Forms is not loaded in this section. enable its inline app block to submit your request.');
        return;
      }
      // Capture Shopify's current step BEFORE the native button is clicked.
      lastNativeStep = found.appSteps.map(node => node.getAttribute('data-current-step')).join('|');
      nativeSubmissionPending = true;
      monitorShopifyResult();
      if (formWatchTimer) clearInterval(formWatchTimer);
      formWatchTimer = setInterval(monitorShopifyResult, 700);
      // Genuine native Forms submit button, invoked as part of the user's click
      // rather than a separate AJAX contact request or API imitation.
      try { nativeButton.click(); }
      catch (error) {
        console.warn('[A2M Notify] Native Shopify Forms action unavailable', error);
        revealShopifyNative('please submit your request using Shopify Forms below.');
        return;
      }
      if (!shopifyConfirmed) {
        showStage('submitting', false);
        nativeFallbackTimer = setTimeout(() => {
          if (!shopifyConfirmed) revealShopifyNative(
            'Shopify has not confirmed your request yet. please complete or check the native form below.'
          );
        }, 8000);
      }
    });

    // Prevent Enter in the custom details from navigating to /contact.
    root.querySelectorAll('input, textarea, select').forEach(field => {
      field.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
          event.preventDefault();
          submit.click();
        }
      });
    });

    get('[data-a2mn-back-details]')?.addEventListener('click', () => {
      if (switching) return;
      // The Shopify request remains saved even if SMS is skipped.
      showStage(shopifyConfirmed ? 'shopify' : 'details', false);
    });
    get('[data-a2mn-skip-sms]')?.addEventListener('click', () => {
      if (switching || !shopifyConfirmed) return;
      showStage('shopify');
    });
    get('[data-a2mn-back-sms]')?.addEventListener('click', () => {
      if (switching) return;
      showStage(shopifyConfirmed ? 'sms' : 'details', false);
    });
    get('[data-a2mn-back-to-request]')?.addEventListener('click',()=>{
      if(switching)return;
      if(!shopifyConfirmed){nativeSubmissionPending=false;stopShopifyWatch();}
      showStage('details',false);
    });
    get('[data-a2mn-history-clear]')?.addEventListener('click',()=>{
      storeRecentHistory([]);
      drawRecentHistory();
    });
    get('[data-a2mn-summary-toggle]')?.addEventListener('click',()=>{
      const button=get('[data-a2mn-summary-toggle]');
      const content=get('[data-a2mn-summary-content]');
      const expanded=button.getAttribute('aria-expanded')==='true';
      button.setAttribute('aria-expanded',String(!expanded));
      content.hidden=expanded;
      get('[data-a2mn-summary-icon]').textContent=expanded?'+':'−';
    });
    get('[data-a2mn-continue-shopping]')?.addEventListener('click',()=>{
      const drawer=host.closest('[data-a2mn-preview-root]');
      if (drawer) {
        // Preview keeps its existing behavior: close this drawer.
        drawer.querySelector('[data-a2mn-close]')?.click();
        return;
      }
      // Full-page Notify Me has an independent, editable success destination.
      const page=host.closest('[data-a2mn-page]');
      const destination=page?.getAttribute('data-a2mn-success-destination') || '/';
      window.location.assign(destination);
    });

    // Read query-prefill values only; never submit during initialization.
    const params = new URLSearchParams(location.search);
    const requested = params.get('mode');
    if (['restock', 'notify', 'early'].includes(requested)) mode = requested;
    const selected = params.get('product');
    if (selected) {
      const match = [...product.options].find(option =>
        option.value.toLowerCase() === selected.toLowerCase() || option.dataset.handle === selected
      );
      if (match) {
        product.value = match.value;
        product.dispatchEvent(new Event('change'));
      }
    }
    updateFields();
    updateButtonReadiness();

    controllers.push({onVerifiedPostscript, getStage: () => stage, host, appSlot, refreshShopifyFormAvailability});
    refreshShopifyFormAvailability();
  }

  function onPostscriptEvent(event) {
    const detail = event && event.detail;
    if (!detail || detail.type !== 'complete') return;
    if (String(detail.shopId) !== POSTSCRIPT_SHOP || String(detail.formId) !== POSTSCRIPT_FORM) return;
    // Official Postscript completion event, not a time-based guess.
    // Completion can also represent a previously subscribed customer.
    postscriptCompletion = detail;
    for (const controller of controllers) {
      controller.onVerifiedPostscript(detail);
    }
  }

  function ensurePostscriptSDK() {
    if (!document.querySelector('postscript-embedded-form')) return;
    if (document.querySelector('script[src*="embeddedForm.bundle.js"]')) return;
    const script = document.createElement('script');
    script.src = 'https://sdk.postscript.io/embeddedForm.bundle.js';
    script.async = true;
    script.dataset.a2mnPostscriptSdk = '1';
    document.head.appendChild(script);
  }


  /* v10 visual-only overrides. Never change SDK phone layout / opt-in logic. */
  const POSTSCRIPT_SHADOW_STYLE_ID = 'a2mn-postscript-shadow-v16';
  const POSTSCRIPT_SHADOW_CSS = `
    :host {display:block!important;width:100%!important;max-width:100%!important;color-scheme:light;}
    form {width:100%!important;max-width:100%!important;margin-inline:auto!important;}
    /* Scoped to the class observed in Chrome DevTools. One full-height
       border, and phone input fills that same pill instead of a thin bar. */
    .ps-phone-input-block-iti-wrapper,
    .a2mn-ps-phone-shell {
      box-sizing:border-box!important;
      width:100%!important;max-width:100%!important;
      height:var(--a2mn-ps-field-height,76px)!important;
      min-height:var(--a2mn-ps-field-height,76px)!important;
      border:1px solid var(--a2mn-ps-border,#cfcfcf)!important;
      border-radius:999px!important;
      background:var(--a2mn-ps-field-bg,#fff)!important;
      overflow:visible!important;box-shadow:none!important;
    }
    /* V17: keep phone field geometry; replace the iOS/SDK blue focus ring
       with one subtle, 1px A2M outline. Edit the color value to #cfcfcf
       for no visible focus change, or transparent to hide the border on focus. */
    .ps-phone-input-block-iti-wrapper,
    .a2mn-ps-phone-shell {
      outline:none!important;
      outline-offset:0!important;
      box-shadow:none!important;
    }
    .ps-phone-input-block-iti-wrapper:focus-within,
    .a2mn-ps-phone-shell:focus-within {
      border-width:1px!important;
      border-style:solid!important;
      border-color:var(--a2mn-phone-focus-color,#a0a0a0)!important;
      outline:none!important;
      box-shadow:none!important;
    }
    .ps-phone-input-block-iti-wrapper input[type="tel"]:focus,
    .ps-phone-input-block-iti-wrapper input[type="tel"]:focus-visible,
    .a2mn-ps-phone-shell .a2mn-ps-phone-input:focus,
    .a2mn-ps-phone-shell .a2mn-ps-phone-input:focus-visible {
      outline:none!important;
      box-shadow:none!important;
    }
    .ps-phone-input-block-iti-wrapper input[type="tel"],
    .a2mn-ps-phone-shell .a2mn-ps-phone-input {
      box-sizing:border-box!important;
      height:calc(var(--a2mn-ps-field-height,76px) - 2px)!important;
      min-height:0!important;max-height:none!important;
      padding-top:0!important;padding-bottom:0!important;
      margin-top:0!important;margin-bottom:0!important;
      background:transparent!important;background-color:transparent!important;
      border:0!important;border-radius:inherit!important;
      outline:none!important;box-shadow:none!important;
      color:#111!important;font-size:18px!important;line-height:normal!important;
      /* Preserve native horizontal padding for the SDK country selector. */
    }
    /* No forced wrapper flex, fixed flag positions, or hidden country inputs. */
    .a2mn-ps-primary {
      box-sizing:border-box!important;display:inline-flex!important;flex-direction:row!important;
      align-items:center!important;justify-content:center!important;flex-wrap:nowrap!important;
      gap:var(--a2mn-action-arrow-gap,12px)!important;width:100%!important;min-width:100%!important;max-width:none!important;
      height:var(--a2mn-action-height,76px)!important;
      min-height:var(--a2mn-action-height,76px)!important;
      max-height:var(--a2mn-action-height,76px)!important;
      padding:0 var(--a2mn-action-pad-x,24px)!important;margin-top:20px!important;
      border:0!important;border-radius:var(--a2mn-action-radius,60px)!important;
      background:var(--a2mn-ps-button-bg,#111)!important;color:var(--a2mn-ps-button-fg,#fff)!important;
      box-shadow:none!important;font-family:A2MNotifyThin,-apple-system,sans-serif!important;
      font-size:var(--a2mn-action-font-size,26px)!important;
      font-weight:200!important;line-height:1.2!important;text-transform:lowercase!important;
      letter-spacing:-.01em!important;white-space:nowrap!important;text-align:center!important;
    }
    .a2mn-ps-primary::after {content:'→';display:inline-block;flex:none;
      font:100 var(--a2mn-action-arrow-size,32px)/1 A2MNotifyThin,-apple-system,sans-serif;}
    .a2mn-ps-primary:disabled {opacity:.55!important;cursor:not-allowed!important;}
    :is([class*="consent" i],[class*="disclaimer" i],[class*="legal" i],
       [class*="compliance" i],p:has(a[href*="privacy" i]),p:has(a[href*="terms" i])) {
      font:400 var(--a2mn-ps-consent-size,12px)/1.5 Helvetica,Arial,sans-serif!important;
      color:#666!important;
    }
    a {color:var(--a2mn-ps-link,#111)!important;text-decoration:underline!important;}
    .a2mn-ps-code-input {box-sizing:border-box!important;width:100%!important;min-height:68px!important;
      border:1px solid var(--a2mn-ps-border,#cfcfcf)!important;border-radius:22px!important;
      background:#fff!important;color:#111!important;text-align:center!important;
      font:200 24px/1.25 A2MNotifyThin,-apple-system,sans-serif!important;
      letter-spacing:.3em!important;box-shadow:none!important;}
    .a2mn-ps-code-input:focus {border-color:#111!important;box-shadow:0 0 0 1px #111!important;}
    .a2mn-ps-duplicate-otp-copy {display:none!important;}
    .a2mn-ps-resend,.a2mn-ps-resend * {font-family:A2MNotifyThin,-apple-system,sans-serif!important;
      font-weight:200!important;letter-spacing:normal!important;}
    /* Final size rule overrides SDK default heights; reads the section
       variable (full page or drawer) instead of hardcoding 76px. */
    .a2mn-ps-primary {
      box-sizing:border-box!important;
      height:var(--a2mn-action-height,76px)!important;
      min-height:var(--a2mn-action-height,76px)!important;
      max-height:var(--a2mn-action-height,76px)!important;
      padding:0 var(--a2mn-action-pad-x,24px)!important;
      font-size:var(--a2mn-action-font-size,26px)!important;
      gap:var(--a2mn-action-arrow-gap,12px)!important;
    }
  `;

  function setupPostscriptStyles() {
    const observed = new WeakSet();
    let queued = false;

    function stylePhone(input) {
      if (input.classList.contains('a2mn-ps-phone-v16')) return;
      input.classList.add('a2mn-ps-phone-v16', 'a2mn-ps-phone-input');
      // Chrome DevTools confirms this is Postscript's native phone wrapper.
      // Mark precisely ONE wrapper. The old upward ancestor search created
      // a second, oversized border around a skinny internal phone row.
      const wrapper = input.closest('.ps-phone-input-block-iti-wrapper');
      if (wrapper) {
        wrapper.classList.add('a2mn-ps-phone-shell');
        input.classList.remove('a2mn-ps-phone-standalone');
        // Remove stale classes from older hot-reloaded versions.
        for (let node=input.parentElement; node && node!==wrapper; node=node.parentElement) {
          node.classList.remove('a2mn-ps-phone-inner','a2mn-ps-phone-shell');
        }
      } else {
        // Without a verified wrapper, leave the third-party layout alone.
        input.classList.remove('a2mn-ps-phone-standalone');
      }
    }

    function decorate(root) {
      if (!root?.querySelectorAll) return;
      for (const button of root.querySelectorAll('button')) {
        const label = (button.textContent || '').replace(/\s+/g,' ').trim().toLowerCase();
        if (/^(subscribe|submit code|verify number|verify code)(?:\s*[→➜➝])?$/.test(label)) {
          button.classList.add('a2mn-ps-primary');
        }
        if (/resend\s*code/i.test(label)) button.classList.add('a2mn-ps-resend');
      }
      for (const el of root.querySelectorAll('a,span,p,div')) {
        if (el.children.length > 0 && el.childElementCount > 1) continue;
        const content = (el.textContent || '').trim().toLowerCase();
        if (/^(resend code|resend code in \d+[sm]|didn.t get a code\??)$/.test(content)) {
          el.classList.add('a2mn-ps-resend');
        }
      }
      for (const input of root.querySelectorAll('input')) {
        const autocomplete = (input.getAttribute('autocomplete')||'').toLowerCase();
        const type = (input.getAttribute('type')||'').toLowerCase();
        const name = (input.getAttribute('name')||'').toLowerCase();
        if (type==='tel'||autocomplete.startsWith('tel')||/phone/.test(name)) stylePhone(input);
        else if (autocomplete==='one-time-code'||
          ((type==='text'||type==='number'||!type)&&(/^(4|6)$/.test(input.getAttribute('maxlength')||'')||/(otp|verification|code)/.test(name)))) {
          input.classList.add('a2mn-ps-code-input');
        }
      }
    }

    function inject(root) {
      if (!root?.querySelector) return;
      if (!root.querySelector('#'+POSTSCRIPT_SHADOW_STYLE_ID)) {
        const style=document.createElement('style');
        style.id=POSTSCRIPT_SHADOW_STYLE_ID;
        style.textContent=POSTSCRIPT_SHADOW_CSS;
        root.appendChild(style);
      }
      decorate(root);
    }
    function walk(root,depth=0) {
      if (!root?.querySelectorAll||depth>5) return;
      decorate(root);
      for (const element of root.querySelectorAll('*')) {
        if (element.shadowRoot) {
          inject(element.shadowRoot);
          if (!observed.has(element.shadowRoot)) {
            observed.add(element.shadowRoot);
            observer.observe(element.shadowRoot,{childList:true,subtree:true});
          }
          walk(element.shadowRoot,depth+1);
        }
        if (element.tagName==='IFRAME') {
          try {
            const doc=element.contentDocument;
            if (doc?.documentElement) {
              if (!doc.getElementById(POSTSCRIPT_SHADOW_STYLE_ID)) {
                const style=doc.createElement('style');
                style.id=POSTSCRIPT_SHADOW_STYLE_ID;
                style.textContent=POSTSCRIPT_SHADOW_CSS;
                doc.head?.appendChild(style);
              }
              walk(doc,depth+1);
            }
          } catch (_) { /* cross-origin iframe is not accessible */ }
        }
      }
    }
    function ownerBackSetting(stage, step) {
      const owner = stage.closest('[data-a2mn-preview-root],[data-a2mn-page]');
      const raw = owner?.dataset?.[step === 'code' ? 'a2mnCodeShowBack' : 'a2mnPhoneShowBack'];
      return raw === 'true' ? 'true' : 'false';
    }
    function syncSmsStepCopy(host, shadow) {
      const stage = host.closest('[data-a2mn-sms-native]');
      const copy = stage?.querySelector('.a2mn-sms-explainer');
      if (!copy || !shadow) return;
      // The native Postscript verification UI uses four-digit codes. Detect
      // the rendered code-submit action, not a timer or a guessed event.
      const codeButton = Array.from(shadow.querySelectorAll('button'))
        .some(button => /^(submit code|verify code|verify number)(?:\s*[→➜➝])?$/i
          .test((button.textContent || '').replace(/\s+/g, ' ').trim()));
      const codeInput = !!shadow.querySelector('input[autocomplete="one-time-code"]');
      const isOtp = codeButton || codeInput;
      if (stage.dataset.a2mnOtpCopy === String(isOtp)) return;
      stage.dataset.a2mnOtpCopy = String(isOtp);
      stage.dataset.a2mnSmsStep = isOtp ? 'code' : 'phone';
      stage.dataset.a2mnBackVisible = (isOtp ? ownerBackSetting(stage, 'code') : ownerBackSetting(stage, 'phone'));
      const owner=stage.closest('[data-a2mn-preview-root],[data-a2mn-page]');
      const config=owner?.dataset||{};
      const title=stage.querySelector('.a2mn-sms-heading span');
      const saved=stage.querySelector('[data-a2mn-saved-text]');
      const back=stage.querySelector('[data-a2mn-sms-back-label]');
      const skip=stage.querySelector('[data-a2mn-sms-skip-label]');
      const savedNote=stage.querySelector('[data-a2mn-saved-prompt] .a2mn-inline-note');
      const desiredPrefix=isOtp?'a2mnCode':'a2mnPhone';
      const read=(field,fallback)=>config[desiredPrefix+field]||fallback;
      if(title)title.textContent=read('Heading',config.v24SmsHeading||'verify your text updates');
      if(saved)saved.textContent=read('Saved',config.v24SmsSavedCopy||'your request has been submitted. text updates are optional.');
      if(back)back.textContent=read('Back',config.v24SmsBackCopy||'back to my request');
      if(skip)skip.textContent=read('Skip',config.v24SmsSkipCopy||'skip text updates & view request confirmation');
      stage.dataset.a2mnSmsBlockGlyph=read('Note',config.v24SmsNoteShow||'true');
      stage.dataset.a2mnSmsBlockSavedGlyph=read('SavedNote',config.v24SavedNoteShow||'true');
      if(savedNote)savedNote.hidden=stage.dataset.a2mnSmsBlockSavedGlyph==='false';
      if (isOtp) {
        copy.replaceChildren();
        const instruction = document.createElement('span');
        instruction.className = 'a2mn-otp-instruction';
        instruction.textContent = config.a2mnCodeInstructions || config.v24SmsCodeCopy || 'to validate your phone number, we sent you a one-time confirmation code.';
        const prompt = document.createElement('span');
        prompt.className = 'a2mn-otp-prompt';
        prompt.textContent = config.a2mnCodeResend || config.v24SmsResendCopy || 'didn’t get a code?';
        copy.append(instruction, prompt);
        // Leave Postscript's own resend countdown/link and validation intact.
      } else {
        copy.textContent = config.a2mnPhoneDescription || config.v24SmsCopy || 'enter your number to receive restock alerts, early access, free priority shipping, & exclusive offers up to 50% off.';
      }
    }
    function hideDuplicateOtpPrompts(root) {
      if (!root?.querySelectorAll) return;
      for (const el of root.querySelectorAll('p,span,div')) {
        if (el.childElementCount !== 0) continue;
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        const duplicated = /^(to validate your phone number, we sent you a one-time confirmation code\.|didn.t get a code\?)$/i.test(text);
        if (duplicated) el.classList.add('a2mn-ps-duplicate-otp-copy');
      }
    }
    function scan() {
      queued=false;
      document.querySelectorAll('[data-a2mn-postscript-style="true"] .a2mn-sms-native postscript-embedded-form')
        .forEach(host => {
          if (host.shadowRoot) {
            syncSmsStepCopy(host, host.shadowRoot);
            hideDuplicateOtpPrompts(host.shadowRoot);
            inject(host.shadowRoot);
            if (!observed.has(host.shadowRoot)) {
              observed.add(host.shadowRoot);
              observer.observe(host.shadowRoot,{childList:true,subtree:true});
            }
            walk(host.shadowRoot);
          } else {
            syncSmsStepCopy(host, host);
            walk(host);
          }
        });
    }
    function queueScan() {
      if (queued) return;
      queued=true;
      requestAnimationFrame(scan);
    }
    const observer=new MutationObserver(queueScan);
    observer.observe(document.documentElement,{childList:true,subtree:true});
    queueScan();
    // A custom element may attach its ShadowRoot without changing the light
    // DOM, and React may switch Postscript steps inside that root. A small,
    // stage-limited poll detects those updates without interfering with SMS.
    window.setInterval(() => {
      if (document.querySelector('[data-a2mn-sms-native]:not([hidden]) postscript-embedded-form')) {
        queueScan();
      }
    }, 450);
  }

  /* Shopify Forms: claim a single misplaced Form app block on the Notify Me
     page, instead of leaving its "Sign up" interface visible under details.
     Moving an existing node preserves its listener and native success state.
     It does not submit anything or alter customer consent. */
  function setupShopifyFormsPlacement() {
    if (!/\/pages\/notify-me\/?$/.test(location.pathname)) return;
    let pending=false;
    const findAppBlock = () => [...document.querySelectorAll('.shopify-app-block')].filter(block =>
      !block.closest('[data-a2mn-stage-host]')
    );
    const looksLikeForms = block => {
      const text=(block.textContent||'').toLowerCase();
      const markup=block.outerHTML.slice(0,12000).toLowerCase();
      return /(shopify.?forms|form.?id[=\"' ]+1219696|forms-app)/.test(markup)||
        (/sign up|subscribe|join the list/.test(text) && Boolean(block.querySelector('input[type="email"],input[autocomplete="email"],form')));
    };
    function claim() {
      pending=false;
      const pageHost=document.querySelector('.a2mn-page [data-a2mn-stage-host]');
      const slot=pageHost?.querySelector('[data-a2mn-shopify-app-block]');
      if (!slot) return;
      if (slot.querySelector('.shopify-app-block')) return;
      const outsiders=findAppBlock();
      const matches=outsiders.filter(looksLikeForms);
      const candidate=matches.length===1 ? matches[0]
        : outsiders.length===1 ? outsiders[0] : null;
      if (!candidate) return;
      candidate.dataset.a2mnAdoptedForms='1';
      slot.appendChild(candidate);
      window.dispatchEvent(new Event('a2mn:forms-placement-updated'));
    }
    function queue() {
      if (pending) return;
      pending=true;
      requestAnimationFrame(claim);
    }
    const observer=new MutationObserver(queue);
    observer.observe(document.documentElement,{subtree:true,childList:true});
    window.addEventListener('a2mn:claim-shopify-form',claim);
    queue();
  }

  function initDrawer() {
    const overlay = document.querySelector('[data-a2mn-preview-root]');
    if (!overlay) return null;

    // The Notify section is often placed inside Shopify's footer group.
    // Its ancestors can form a containing/stacking context (transform,
    // containment or overflow), trapping a position:fixed drawer. Portal
    // the live-store drawer to <body>, keeping the section DOM, all settings,
    // input state and third-party forms intact. Leave it in place in editor
    // mode so section selection and re-rendering continue to work.
    if (!window.Shopify?.designMode && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    // A2M Page Preview currently uses z-index: 2147483000.
    // Notify must render above it if both routes are triggered.
    overlay.style.zIndex = '2147483500';

    if (overlay.dataset.a2mnDrawerReady === '1') {
      if (typeof overlay.__a2mnOpen === 'function') return overlay.__a2mnOpen;
      // Shopify can rehydrate a section with its old data attributes but
      // without JS closures. Initialize that element instead of giving up.
      delete overlay.dataset.a2mnDrawerReady;
    }

    const sheet = overlay.querySelector('[data-a2mn-sheet]');
    const scroll = overlay.querySelector('[data-a2mn-scroll]');
    const grip = overlay.querySelector('[data-a2mn-grip-zone]');
    const loading = overlay.querySelector('[data-a2mn-loading]');
    // A Shopify section may be temporarily incomplete during editor re-render.
    // Do not mark it initialized until the full drawer DOM exists.
    if (!sheet || !scroll || !grip || !loading) return null;
    overlay.dataset.a2mnDrawerReady = '1';

    const start = Math.min(80, Math.max(30, Number(overlay.dataset.startHeight) || 55));
    const expanded = Math.min(100, Math.max(85, Number(overlay.dataset.expandedHeight) || 96));
    const threshold = Number(overlay.dataset.snapThreshold) || 55;
    let state = 'closed';
    let closeTimer = 0;
    let loadTimer = 0;
    let drag = null;
    let priorFocus = null;
    // Keep the original scroll coordinates & inline styles to restore exactly.
    // Mobile Safari can scroll a background body despite overflow:hidden.
    const pageLock = {active:false,y:0,body:{},html:{}};
    const lockProps = ['position','top','left','right','width','overflow','overscroll-behavior'];
    function freezeBackground() {
      if (pageLock.active) return;
      pageLock.y = window.scrollY || document.documentElement.scrollTop || 0;
      const body = document.body, html=document.documentElement;
      lockProps.forEach(prop => {
        pageLock.body[prop]=[body.style.getPropertyValue(prop),body.style.getPropertyPriority(prop)];
        pageLock.html[prop]=[html.style.getPropertyValue(prop),html.style.getPropertyPriority(prop)];
      });
      pageLock.active=true;
      html.classList.add('a2mn-background-locked');
      html.style.setProperty('overflow','hidden');
      html.style.setProperty('overscroll-behavior','none');
      body.style.setProperty('position','fixed');
      body.style.setProperty('top',`-${pageLock.y}px`);
      body.style.setProperty('left','0');
      body.style.setProperty('right','0');
      body.style.setProperty('width','100%');
      body.style.setProperty('overflow','hidden');
      body.style.setProperty('overscroll-behavior','none');
    }
    function unfreezeBackground() {
      if (!pageLock.active) return;
      pageLock.active=false;
      lockProps.forEach(prop => {
        if(pageLock.body[prop][0]) document.body.style.setProperty(prop,...pageLock.body[prop]);
        else document.body.style.removeProperty(prop);
        if(pageLock.html[prop][0])document.documentElement.style.setProperty(prop,...pageLock.html[prop]);
        else document.documentElement.style.removeProperty(prop);
      });
      document.documentElement.classList.remove('a2mn-background-locked');
      window.scrollTo(0,pageLock.y);
    }


    function setHeight(value) {
      sheet.style.setProperty('--a2mn-height', value + 'dvh');
    }
    function setFooterState() {
      overlay.dataset.a2mnDrawerState = state;
      const control = overlay.querySelector('[data-a2mn-expand]');
      if (!control) return;
      const label = control.querySelector('[data-a2mn-expand-text]');
      const arrow = control.querySelector('[data-a2mn-expand-arrow]');
      if (label) label.textContent = state === 'expanded'
        ? (control.dataset.a2mnCollapseLabel || 'back to preview')
        : (control.dataset.a2mnExpandLabel || 'expand').replace(/[↑→]/g, '').trim();
      if (arrow) arrow.textContent = state === 'expanded' ? '↓' : '↑';
    }
    function expand() {
      if (state === 'closed') return;
      state = 'expanded';
      setHeight(expanded);
      setFooterState();
    }
    function collapse() {
      if (state === 'closed') return;
      state = 'peek';
      setHeight(start);
      setFooterState();
    }
    function open() {
      if (state !== 'closed') {
        // If another preview took over visibility, reassert our layer.
        overlay.hidden = false;
        overlay.classList.add('is-visible');
        freezeBackground();
        return;
      }
      clearTimeout(closeTimer);
      clearTimeout(loadTimer);
      priorFocus = document.activeElement;
      freezeBackground();
      overlay.hidden = false;
      sheet.setAttribute('aria-hidden', 'false');
      state = 'peek';
      setHeight(start);
      setFooterState();
      scroll.scrollTop = 0;
      syncKeyboardViewport();
      requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('is-visible')));
      const loaderMode = overlay.dataset.loaderMode;
      if (loaderMode && loaderMode !== 'none') {
        loading.hidden = false;
        loading.classList.remove('is-fading');
        clearTimeout(loadTimer);
        loadTimer = setTimeout(() => {
          loading.classList.add('is-fading');
          const fadeTime = Number(getComputedStyle(overlay).getPropertyValue('--a2mn-loader-fade-out').replace('ms',''));
          setTimeout(() => { loading.hidden = true; }, Number.isFinite(fadeTime) ? fadeTime : 250);
        }, Number(overlay.dataset.loaderDuration) || 500);
      }
      sheet.querySelector('[data-a2mn-close]')?.focus({preventScroll: true});
    }
    function close() {
      if (state === 'closed') return;
      state = 'closed';
      clearTimeout(loadTimer);
      // Avoid Safari auto-scrolling the page when a keyboard field closes.
      if (sheet.contains(document.activeElement)) document.activeElement.blur?.();
      overlay.classList.remove('is-visible');
      sheet.setAttribute('aria-hidden', 'true');
      syncKeyboardViewport();
      stopKinetic();
      const duration = parseFloat(getComputedStyle(overlay).getPropertyValue('--a2mn-animation-ms')) || 350;
      closeTimer = setTimeout(() => {
        if (state !== 'closed') return;
        overlay.hidden = true;
        loading.hidden = true;
        unfreezeBackground();
        priorFocus?.focus?.({preventScroll: true});
      }, duration + 30);
    }

    // Touches outside the active sheet must never scroll the storefront.
    // Inside the sheet, leave native vertical scrolling, selects and inputs alone.
    document.addEventListener('touchmove', event => {
      if (state === 'closed' || !event.cancelable) return;
      const target = event.target;
      if (!(target instanceof Node) || !sheet.contains(target)) event.preventDefault();
    }, {capture:true,passive:false});

    overlay.querySelectorAll('[data-a2mn-close]').forEach(button => button.addEventListener('click', close));
    overlay.querySelector('[data-a2mn-expand]')?.addEventListener('click', () => { if (state === 'expanded') collapse(); else expand(); });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && state !== 'closed') close();
    });
    // The link interception lives on window in CAPTURE phase below.
    // Our separate A2M Page Preview also listens on document in capture phase
    // and calls stopImmediatePropagation(). A document/bubble handler here
    // never gets a chance to open this specific drawer.
    overlay.__a2mnOpen = open;

    grip.addEventListener('pointerdown', event => {
      if (event.button !== 0 || state === 'closed') return;
      drag = {id: event.pointerId, y: event.clientY, start: event.clientY, from: state};
      grip.setPointerCapture(event.pointerId);
      overlay.classList.add('is-dragging');
    });
    grip.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      drag.y = event.clientY;
      const delta = drag.y - drag.start;
      const base = drag.from === 'expanded' ? expanded : start;
      setHeight(Math.max(25, Math.min(expanded, base - delta / window.innerHeight * 100)));
    });
    function finishDrag(event) {
      if (!drag || drag.id !== event.pointerId) return;
      const previous = drag;
      drag = null;
      overlay.classList.remove('is-dragging');
      const delta = previous.y - previous.start;
      if (delta < -threshold) expand();
      else if (delta > threshold && previous.from === 'expanded') {
        collapse();
      } else if (delta > threshold * 1.6 && previous.from === 'peek') close();
      else setHeight(state === 'expanded' ? expanded : start);
    }
    grip.addEventListener('pointerup', finishDrag);
    grip.addEventListener('pointercancel', finishDrag);

    // V25: restore native, momentum-enabled iOS scrolling by default.
    // V16-V18 used preventDefault + a 0.45 movement multiplier, causing
    // visibly sluggish scrolling in BOTH directions. The grip remains an
    // independent drag target; native forms and Postscript stay untouched.
    const scrollSpeedSetting = Number(overlay.dataset.a2mnScrollSpeed);
    // The old V16-V24 value (usually 45) must NOT remain a slowdown after
    // upgrading: anything 100 or lower now selects normal native scrolling.
    const scrollSpeed = Number.isFinite(scrollSpeedSetting) && scrollSpeedSetting > 100
      ? Math.min(2, scrollSpeedSetting / 100) : 1;
    let touchStart = null;
    let kineticFrame = 0;
    let kineticVelocity = 0;
    let kineticTime = 0;
    function stopKinetic() {
      if (kineticFrame) cancelAnimationFrame(kineticFrame);
      kineticFrame = 0;
      kineticVelocity = 0;
    }
    function kineticStep(time) {
      const elapsed = kineticTime ? Math.min(48, time - kineticTime) : 16;
      kineticTime = time;
      const oldTop = scroll.scrollTop;
      scroll.scrollTop += kineticVelocity * elapsed;
      kineticVelocity *= Math.pow(.93, elapsed / 16);
      if (Math.abs(kineticVelocity) > .06 && scroll.scrollTop !== oldTop) {
        kineticFrame = requestAnimationFrame(kineticStep);
      } else {
        stopKinetic();
      }
    }
    scroll.addEventListener('touchstart', event => {
      stopKinetic();
      if (event.touches.length !== 1) {touchStart = null;return;}
      const element = event.target instanceof Element ? event.target : null;
      const path = event.composedPath?.() || [];
      const onEmbed = path.some(node => node instanceof Element &&
        node.matches?.('postscript-embedded-form,shopify-forms'));
      // A swipe begun on a tab is reserved for the pill selector. Stop any
      // residual kinetic scrolling, but don't create a drawer-scroll gesture.
      if (element?.closest('.a2mn-tabs')) {
        touchStart = null;
        return;
      }
      const interactive = !!element?.closest(
        'input,textarea,select,button,a,[contenteditable="true"],postscript-embedded-form,shopify-forms,.shopify-app-block'
      ) || onEmbed;
      const now=performance.now();
      touchStart = {y:event.touches[0].clientY, initialY:event.touches[0].clientY,
        lastTime:now, velocity:0, manual:state === 'expanded' && !interactive &&
        Math.abs(scrollSpeed - 1) > .02,
        suppress:false, interactive};
    }, {passive:true});
    scroll.addEventListener('touchmove', event => {
      if (!touchStart || event.touches.length !== 1) return;
      const currentY = event.touches[0].clientY;
      const movement = touchStart.y - currentY;
      const fromStart = currentY - touchStart.initialY;
      const now = performance.now();
      const dt = Math.max(8, now - touchStart.lastTime);
      touchStart.y = currentY;
      touchStart.lastTime = now;
      if (touchStart.suppress) {
        if (event.cancelable) event.preventDefault();
        return;
      }
      if (state === 'peek' && fromStart < -18 && !touchStart.interactive) {
        expand();
        // One swipe expands the drawer; the next swipe scrolls the content.
        touchStart.suppress = true;
        if (event.cancelable) event.preventDefault();
        return;
      }
      if (state === 'expanded' && touchStart.manual) {
        if (event.cancelable) event.preventDefault();
        const distance = movement * scrollSpeed;
        scroll.scrollTop += distance;
        touchStart.velocity = .55 * (distance / dt) + .45 * touchStart.velocity;
      }
      // At 100%, do NOT preventDefault: Safari's native fast flick scrolling
      // and deceleration are substantially smoother than manual touchmove.
    }, {passive:false});
    scroll.addEventListener('touchend', () => {
      if (touchStart?.manual && !touchStart.suppress &&
          Math.abs(touchStart.velocity) > .12 && !reducedMotion()) {
        kineticVelocity = Math.max(-2.4,Math.min(2.4,touchStart.velocity));
        kineticTime = 0;
        kineticFrame = requestAnimationFrame(kineticStep);
      }
      touchStart = null;
    }, {passive:true});
    scroll.addEventListener('touchcancel', () => { touchStart = null;stopKinetic(); }, {passive:true});

    // iOS Safari focuses inputs by shrinking its *visual* viewport. Keep the
    // drawer flush against the keyboard instead of exposing the page below.
    const visual = window.visualViewport;
    function syncKeyboardViewport() {
      if (state === 'closed' || !visual) {
        overlay.dataset.a2mnKeyboard = 'false';
        overlay.style.removeProperty('--a2mn-keyboard-bottom');
        overlay.style.removeProperty('--a2mn-visual-height');
        return;
      }
      const focusNode = document.activeElement;
      const isInput = focusNode instanceof Element && sheet.contains(focusNode) &&
        focusNode.matches('input,textarea,select,[contenteditable="true"]');
      const obscured = Math.max(0, window.innerHeight - visual.offsetTop - visual.height);
      const keyboardOpen = isInput && obscured > 130;
      overlay.dataset.a2mnKeyboard = String(keyboardOpen);
      overlay.style.setProperty('--a2mn-keyboard-bottom', keyboardOpen ? obscured+'px' : '0px');
      overlay.style.setProperty('--a2mn-visual-height', Math.max(180,visual.height)+'px');
    }
    if (visual) {
      visual.addEventListener('resize',syncKeyboardViewport,{passive:true});
      visual.addEventListener('scroll',syncKeyboardViewport,{passive:true});
      sheet.addEventListener('focusin',syncKeyboardViewport);
      sheet.addEventListener('focusout',() => {setTimeout(syncKeyboardViewport,80);});
    }
    return open;
  }

  // Reserved specifically for the Notify Me drawer, BEFORE document-level
  // capture handlers used by A2M Page Preview or the theme's link router.
  // No changes to the separate Page Preview JS or its other page routes.
  function isNotifyOpenClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey) return false;
    const target = event.target;
    // composedPath() supports links wrapped by theme web components.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const pathLink = path.find(item => item instanceof Element &&
      item.matches?.('a[href],[data-a2mn-open]'));
    const link = pathLink || (target instanceof Element
      ? target.closest('a[href],[data-a2mn-open]') : null);
    if (!link || link.closest('[data-a2mn-preview-root]')) return false;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return false;
    if (link.hasAttribute('data-a2mn-open')) return true;
    const href = link.getAttribute('href') || '';
    if (/^shopify:\/\/pages\/notify-me(?:[?#]|$)/i.test(href)) return true;
    try {
      const url = new URL(link.href, location.href);
      return url.origin === location.origin &&
        url.pathname.replace(/\/+$/, '') === '/pages/notify-me';
    } catch (_) { return false; }
  }

  function openNotifyIfAvailable() {
    const open = initDrawer(); // Also handles editor sections inserted after page load.
    if (typeof open !== 'function') {
      if (!document.querySelector('[data-a2mn-preview-root]')) {
        console.warn('[A2M Notify] Drawer markup is missing. Enable A2M Notify Preview in the global Footer group.');
      } else {
        console.warn('[A2M Notify] Drawer markup is incomplete. Check section Liquid and Shopify theme-editor errors.');
      }
      return false;
    }
    open();
    return true;
  }

  // These helpers are intentionally available even on pages which load the
  // drawer after initial script execution.
  window.openA2MNotifyPreview = () => openNotifyIfAvailable();
  // Safe DevTools diagnostic; exposes no customer data or third-party secrets.
  window.a2mNotifyDrawerDiagnostics = () => {
    const root = document.querySelector('[data-a2mn-preview-root]');
    const sheet = root?.querySelector('[data-a2mn-sheet]');
    return {
      scriptLoaded: true,
      drawerPresent: !!root,
      drawerComplete: !!(root && sheet && root.querySelector('[data-a2mn-scroll]') &&
        root.querySelector('[data-a2mn-grip-zone]') && root.querySelector('[data-a2mn-loading]')),
      initialized: root?.dataset.a2mnDrawerReady === '1',
      hidden: root?.hidden ?? null,
      open: !!root && !root.hidden && root.classList.contains('is-visible'),
      display: root ? getComputedStyle(root).display : null,
      zIndex: root ? getComputedStyle(root).zIndex : null,
      parent: root?.parentElement?.tagName || null,
      otherPagePreviewOpen: !!document.querySelector('#a2m-page-preview-root.a2mpp-open')
    };
  };
  window.addEventListener('click', event => {
    if (!isNotifyOpenClick(event)) return;
    // If the global drawer section is absent, allow the normal page URL to
    // work rather than swallowing the click with no visible result.
    if (!openNotifyIfAvailable()) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  let supportingSystemsReady = false;
  function init() {
    // Bring up the drawer FIRST: a missing/partial form must never prevent
    // the open/close, drag handle, or backdrop from working.
    initDrawer();
    document.querySelectorAll('[data-a2mn-ui]').forEach(root => {
      try { initUI(root); }
      catch (error) {
        // A Theme Editor re-render can temporarily create partial markup.
        // Leave it eligible for initialization once the section completes.
        delete root.dataset.ready;
        console.error('[A2M Notify] Form initialization failed; drawer still available', error);
      }
    });
    ensurePostscriptSDK();
    if (!supportingSystemsReady) {
      supportingSystemsReady = true;
      setupPostscriptStyles();
      setupShopifyFormsPlacement();
      window.addEventListener('a2mn:forms-placement-updated', () => {
        controllers.forEach(controller => controller.refreshShopifyFormAvailability());
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => {
    // Dynamic theme-editor sections reuse the same script instance.
    requestAnimationFrame(init);
  });
})();
